import { ChangeDetectionStrategy, Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectItem } from './select-item';
import { SelectionModel } from '@angular/cdk/collections';
import { BaseSelectComponent } from './base-select.component';
import { SelectDropdownService } from '../dropdown/select-dropdown.service';
import { SelectDataWithGroup } from '@pso-ui/select/select.interfaces';

@Component({
  selector: 'app-base-grouped-multi-select',
  templateUrl: './base-grouped-multi-select.component.html',
  styleUrls: ['./base-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BaseGroupedMultiSelectComponent),
      multi: true,
    },
    SelectDropdownService,
  ],
})
export class BaseGroupedMultiSelectComponent extends BaseSelectComponent implements ControlValueAccessor {
  @Input() placeholder = this.translateService.instant('select');
  @Input() grouped = false;
  selectionModel = new SelectionModel<SelectItem<unknown>>(true, [], false, (a, b) => a.getValue() === b.getValue());
  value: any[];

  groupedItems: Record<string, SelectItem<unknown>[]> = {};
  expanded: Record<string, boolean> = {};
  segmentKeys: string[] = [];

  @Input()
  set data(value) {
    const normalized = (value ?? []).map((v: any) => {
      if (v instanceof SelectItem) return v;
      const item = new SelectItem(v.value, v.label);
      (item as any).tyre_class = v.tyre_class;
      return item;
    });

    super.data = normalized;

    if (this.grouped && normalized.length) {
      this.groupItemsBySegment(normalized);
    }
  }

  get data(): SelectDataWithGroup<unknown>[] | (string | number)[] {
    return (this as any)._data;
  }

  groupItemsBySegment(data: any[]): void {
    if (!data) return;

    this.groupedItems = data.reduce(
      (acc, item) => {
        if (!item.tyre_class) return acc;

        const group = item.tyre_class.toLowerCase();
        if (!acc[group]) acc[group] = [];
        acc[group].push(item);
        return acc;
      },
      {} as Record<string, any[]>,
    );

    this.segmentKeys = Object.keys(this.groupedItems);

    this.segmentKeys.forEach((key) => {
      this.expanded[key] = false;
    });
  }

  toggleGroupSelection(group: string): void {
    const groupItems = this.groupedItems[group];
    const allSelected = this.isGroupFullySelected(group);

    if (allSelected) {
      groupItems.forEach((item) => this.selectionModel.deselect(item));
    } else {
      groupItems.forEach((item) => this.selectionModel.select(item));
    }

    this.writeValue(this.selectionModel.selected.map((i) => i.getValue()));
  }

  isGroupFullySelected(group: string): boolean {
    const groupItems = this.groupedItems[group];
    return groupItems.every((item) => this.selectionModel.isSelected(item));
  }

  isGroupPartiallySelected(group: string): boolean {
    const groupItems = this.groupedItems[group];
    const selectedCount = groupItems.filter((item) => this.selectionModel.isSelected(item)).length;

    return selectedCount > 0 && selectedCount < groupItems.length;
  }

  onGroupCheckboxClick(event: MouseEvent, group: string): void {
    event.stopPropagation();
    this.toggleGroupSelection(group);
  }

  toggleGroup(group: string): void {
    this.expanded[group] = !this.expanded[group];
  }

  writeValue(value: unknown): void | undefined {
    if (value && !Array.isArray(value)) {
      value = [value];
    }

    if (Array.isArray(value) && value.length > 0) {
      const items = getOnlyExistingItems(value as [], this.items);
      if (items.length > 0) {
        this.displayValue = items[0].getLabel();
        this.selectionModel.clear();
        this.selectionModel.select(...items);
        this.value = this.selectionModel.selected.map((item: SelectItem<unknown>) => item.getValue());
        this.onChangeFn(this.value);
        this.changeDetectorRef.detectChanges();
        return;
      }
    }

    this.displayValue = '';
    this.value = [];
    this.selectionModel.clear();
    this.onChangeFn(this.value);
    this.changeDetectorRef.detectChanges();
  }

  openDropdown(): void {
    super.openDropdown((item) => {
      this.onItemClick(item);
    });
  }

  onItemRemove(event: any, item: SelectItem<unknown>): void {
    this.selectionModel.deselect(item);
    this.writeValue(this.selectionModel.selected.map((item: SelectItem<unknown>) => item.getValue()));
  }

  onXIconClick(): void {
    this.writeValue(null);
  }

  onItemClick(item: SelectItem<unknown>): void {
    if (this.selectionModel.isSelected(item)) {
      this.onItemRemove(undefined, item);
    } else {
      this.selectionModel.select(item);
      this.writeValue(this.selectionModel.selected.map((item: SelectItem<unknown>) => item.getValue()));
    }
  }

  onSelectAllClick(): void {
    this.selectionModel.select(...this.items);
    this.writeValue(this.selectionModel.selected.map((item: SelectItem<unknown>) => item.getValue()));
  }
}

const getOnlyExistingItems = (arr: [], items: SelectItem<unknown>[]): SelectItem<unknown>[] => {
  return items.filter((item: SelectItem<unknown>) => {
    let has = false;
    arr.forEach((val: unknown) => {
      if (!has) {
        has = item.is(val);
      }
    });
    return has;
  });
};
